/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { InputConfig, ShowCondition, TaskValues } from '../components/form/TaskInputs/types';
import { get, isEqual } from '@utils/lodash';

type ShowConditionValue<T> = 'Checked' | 'Unchecked' | 'undefined' | T;

/**
 * @return propertyValue either a global property value or (if global property is not found)
 * a 'local' property value (such as a repeated input group property).
 * @param property the property to be searched for.
 * @param parentText (optional) a local context to search within if a global property is not found.
 * @param inputValues the input values to search through.
 */
export function getPropertyValue(property: string, inputValues: TaskValues) {
  // TODO(UI-5526): if the property name has a '.' in it, we need to escape `property` with
  // formEscapingUtils.escapeText to find the value in inputValues, since inputValues is the redux
  // form values, and we escape keys before populating redux form. This will enable this functionality to
  // actually work; it's currently broken for nested properties
  // e.g. if property is "CurrentBorrower∙currentAddress∙lineText", we need to partially escape it to
  // into "CurrentBorrower∙currentAddress.lineText" to access the property on inputValues
  const globalValue = get(inputValues, property);
  //   const localValue = parentText ? get(inputValues, `${parentText}.${property}`) : undefined;
  return globalValue;
  //   return isNil(globalValue) ? localValue : globalValue;
}

/**
 * Return whether the given show condition value is fulfilled
 * @param showConditionValue the value of the showCondition
 * @param inputValue the input value to check against
 * @param operation an operation to perform to check if fulfilled
 */
function isFulfilledShowCondition<T>(showConditionValue: ShowConditionValue<T>, inputValue: T) {
  switch (showConditionValue) {
    case 'Checked':
      return !!inputValue;
    case 'Unchecked':
      return !inputValue;
    case 'undefined': // because undefined cannot be serialized
      return inputValue === undefined;
    default: // fall through to handle operation
  }

  return isEqual(inputValue, showConditionValue);
}

/**
 * @return true if the given input should be shown given the input values
 * @param input the input to check
 * @param inputValues the current values of the form
 */
export function shouldShowInput(input: InputConfig, inputValues: TaskValues = {}) {
  if (!input.showCondition) {
    return true;
  }
  const evaluateShowCondition = (condition: ShowCondition): boolean => {
    return isFulfilledShowCondition(condition.value, getPropertyValue(condition.property, inputValues));
  };
  return evaluateShowCondition(input.showCondition);
}

/**
 * @return the list of inputs, barring those with unfulfilled show conditions
 */
export function getFilteredInputs(inputs: InputConfig[], values: TaskValues) {
  return inputs.filter(input => shouldShowInput(input, values));
}
